import type { ReactNode } from "react";
import { Body1, Caption1, Spinner, Subtitle2, Title2, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { AddCircleRegular, ArchiveRegular, CalendarCancelRegular, DataBarVerticalRegular, DocumentBulletListRegular, FolderOpenRegular, RibbonRegular, TagRegular, TaskListLtrRegular } from "@fluentui/react-icons";
import { useData } from "../data/DataProvider";
import { eAlbo, giorniAllaScadenza, loadGare, statoIscrizione, statoVisuale } from "../data/gare";
import { useAsync } from "../lib/useAsync";
import { navigate, type FiltroRapido } from "../lib/nav";
import { Shell } from "../components/Shell";
import { config } from "../config";

const useStyles = makeStyles({
  hero: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXXL,
    flexWrap: "wrap",
    padding: `${tokens.spacingVerticalXL} ${tokens.spacingHorizontalXXL}`,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: "#ffffff",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  logo: { height: "64px", width: "auto", display: "block" },
  heroText: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS, color: "#242424" },
  heroSub: { color: "#616161" },
  sectionTitle: { marginTop: tokens.spacingVerticalM, color: tokens.colorNeutralForeground2 },
  stats: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: tokens.spacingHorizontalM },
  stat: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    textAlign: "left",
    fontFamily: "inherit",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, border: `1px solid ${tokens.colorNeutralStroke1Hover}` },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  statNum: {
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightSemibold,
    fontVariantNumeric: "tabular-nums",
  },
  statWarn: { color: tokens.colorPaletteRedForeground1 },
  statLabel: { color: tokens.colorNeutralForeground2 },
  menu: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: tokens.spacingHorizontalM },
  tile: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalL,
    minHeight: "96px",
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    textAlign: "left",
    fontFamily: "inherit",
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover, border: `1px solid ${tokens.colorBrandStroke2}` },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}` },
  },
  tileIcon: { color: tokens.colorBrandForeground1, flexShrink: 0, fontSize: "32px", lineHeight: 0 },
  tileText: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXXS },
  tileDesc: { color: tokens.colorNeutralForeground3 },
});

/** Schermata iniziale: logo, situazione delle gare e menu delle funzioni. */
export function Welcome() {
  const s = useStyles();
  const data = useData();
  const gestore = data.roles.gestore;
  const gare = useAsync(() => loadGare(data), [data]);
  const raccolta = useAsync(() => data.repository.root(), [data]);

  const attive = (gare.data ?? []).filter((g) => !eAlbo(g) && statoVisuale(g) === "Attiva");
  const albiDaRinnovare = (gare.data ?? []).filter((g) => eAlbo(g) && ["In scadenza", "Scaduta"].includes(statoIscrizione(g))).length;
  const conteggi = {
    attive: attive.length,
    inScadenza: attive.filter((g) => (giorniAllaScadenza(g) ?? Infinity) <= 7).length,
    daValutare: attive.filter((g) => (g.fields.Valutazione ?? "Da valutare") === "Da valutare").length,
    cartelle: attive.filter((g) => g.fields.Valutazione === "Partecipa" && !g.fields.CartellaId).length,
  };
  const vai = (filtro?: FiltroRapido) => navigate(`/gare/attive${filtro ? `?filtro=${filtro}` : ""}`);

  return (
    <Shell title="Gare" home={false}>
      <div className={s.hero}>
        {config.logoUrl && <img className={s.logo} src={config.logoUrl} alt="Logo" />}
        <div className={s.heroText}>
          <Title2>Gare</Title2>
          <Body1 className={s.heroSub}>Gare pubbliche e cartelle di gara · Buongiorno, {data.me.displayName.split(" ")[0]}</Body1>
        </div>
      </div>

      <Subtitle2 className={s.sectionTitle}>Situazione</Subtitle2>
      {gare.error ? (
        <Caption1>Situazione non disponibile.</Caption1>
      ) : !gare.data ? (
        <Spinner size="small" />
      ) : (
        <div className={s.stats}>
          <Stat n={conteggi.attive} label="Gare in corso" onClick={() => vai()} />
          <Stat n={conteggi.inScadenza} label="Scadono entro 7 giorni" warn={conteggi.inScadenza > 0} onClick={() => vai("in-scadenza")} />
          <Stat n={conteggi.daValutare} label="Da valutare" onClick={() => vai("da-valutare")} />
          <Stat n={conteggi.cartelle} label="Cartelle da creare" warn={conteggi.cartelle > 0} onClick={() => vai("cartelle-da-creare")} />
          <Stat n={albiDaRinnovare} label="Albi da rinnovare (30 giorni)" warn={albiDaRinnovare > 0} onClick={() => navigate("/albi?filtro=in-scadenza")} />
        </div>
      )}

      <Subtitle2 className={s.sectionTitle}>Funzioni</Subtitle2>
      <nav className={s.menu} aria-label="Menu">
        <Tile
          icon={<DocumentBulletListRegular />}
          title="Gare in corso"
          desc="Elenco, ricerca e schede delle gare attive"
          onClick={() => navigate("/gare/attive")}
        />
        {gestore && (
          <Tile icon={<AddCircleRegular />} title="Nuova gara" desc="Censisci una gara: il codice OA si assegna da solo" onClick={() => navigate("/nuova")} />
        )}
        <Tile
          icon={<RibbonRegular />}
          title="Albi"
          desc="Iscrizioni agli albi e agli elenchi fornitori, con le scadenze di rinnovo"
          onClick={() => navigate("/albi")}
        />
        <Tile icon={<CalendarCancelRegular />} title="Gare scadute" desc="Gare attive con la scadenza già passata" onClick={() => navigate("/gare/scadute")} />
        <Tile icon={<ArchiveRegular />} title="Archivio" desc="Gare archiviate" onClick={() => navigate("/gare/archiviate")} />
        {(gestore || data.roles.collaboratore) && (
          <Tile
            icon={<DataBarVerticalRegular />}
            title="Report"
            desc="Riepilogo degli esiti ed estrazione in Excel, per periodo, servizio e tipo di ente"
            onClick={() => navigate("/report")}
          />
        )}
        {gestore && (
          <Tile
            icon={<TagRegular />}
            title="Servizi e sigle"
            desc="Servizi oggetto delle gare e sigle usate nei nomi delle cartelle"
            onClick={() => navigate("/servizi")}
          />
        )}
        {gestore && (
          <Tile
            icon={<TaskListLtrRegular />}
            title="Modello checklist"
            desc="Documenti che si chiedono di solito, proposti nelle checklist delle gare"
            onClick={() => navigate("/modello-checklist")}
          />
        )}
        {raccolta.data && (
          <Tile icon={<FolderOpenRegular />} title="Raccolta delle cartelle" desc="Apri in SharePoint la raccolta Repository" href={raccolta.data.webUrl} />
        )}
      </nav>
    </Shell>
  );
}

function Stat({ n, label, warn, onClick }: { n: number; label: string; warn?: boolean; onClick: () => void }) {
  const s = useStyles();
  return (
    <button type="button" className={s.stat} onClick={onClick}>
      <span className={mergeClasses(s.statNum, warn && s.statWarn)}>{n}</span>
      <span className={s.statLabel}>{label}</span>
    </button>
  );
}

function Tile(props: { icon: ReactNode; title: string; desc: string; onClick?: () => void; href?: string }) {
  const s = useStyles();
  const body = (
    <>
      <span className={s.tileIcon}>{props.icon}</span>
      <span className={s.tileText}>
        <Subtitle2>{props.title}</Subtitle2>
        <Caption1 className={s.tileDesc}>{props.desc}</Caption1>
      </span>
    </>
  );
  return (
    <button type="button" className={s.tile} onClick={() => (props.href ? window.open(props.href, "_blank", "noopener") : props.onClick?.())}>
      {body}
    </button>
  );
}
